/**
 * A blog article, rendered from the knowledge base.
 *
 * This page shipped in PR #1046 with one Kyoto article hardcoded as `const`s — deliberately,
 * the same way the country and destination guides shipped, because the data was the other
 * half of the problem. This is that half: the content now arrives as an `ArticleData`
 * document generated from `website/blogs/` (see `../../discover/articleData.ts`).
 *
 * ROUTING
 * Mounted on `/blog/:article`, the URL space the 412 Strapi articles used to hold — decided
 * 2026-08-25, so nothing already indexed has to move. `BlogArticleRoute.tsx` resolves the
 * article and picks this layout or `ItineraryArticle`; the Strapi renderer is gone. The
 * `/blog/destinations/...` routes the prototype used are gone.
 *
 * SEO is owned by this route's `buildHead` in marketingRoutes.tsx, not by this component —
 * matching every other marketing-route page, none of which self-manage <Helmet>.
 */

import { alpha2For } from "components/b2c/explore/exploreCommon";
import type { ArticleData, ArticleNode, ArticleSection } from "components/discover/articleData";
import { renderBlock } from "components/discover/blockRegistry";
import trackEvent from "components/tracking/trackEvent";
import {
	ArticleEmbed,
	ArticleHero,
	ArticlePhoto,
	ArticleSidebar,
	ArticleList,
	ArticleSubHeading,
	ArticleTable,
	DayHeading,
	GuideLangContext,
	GuideUiContext,
	RichText,
	Faq,
	NumberedHeading,
	Para,
	ReadingProgressBar,
	RelatedArticles,
	WaveDownloadCTA,
} from "components/discover/discoverSections";
import DownloadAppQrCard from "components/website/components/DownloadAppQrCard";
import Footer from "components/website/components/Footer";
import InlineInstallCard from "components/website/components/InlineInstallCard";
import LanguageSwitcher from "components/website/components/LanguageSwitcher";
import Navigation from "components/website/components/navigation/Navigation";
import { bylineOf } from "components/website/authorByline";
import { getBlogTranslations } from "components/website/marketing/i18n/pages/blog";
import { t } from "i18n/index";
import { buildLocalizedPath, isMarketingLangCode, localizeHref } from "i18n/urlLocale";
import { useEffect } from "react";
import { useHistory } from "react-router-dom";
import styles from "./DiscoverArticle.module.scss";

/** `t()` has no placeholder support — fill `{name}` style tokens after the lookup. */
const fill = (str: string, vars: Record<string, string>) => str.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? `{${k}}`);

interface DiscoverArticleProps {
	/** locale -> slug for this article's translations, from the knowledge base. */
	localeSlugs?: Record<string, string>;
	/**
	 * The resolved article. Fetching is `BlogArticleRoute`'s job, not this component's: the
	 * route has to be able to fall through to the Strapi renderer when a slug is not a
	 * knowledge-base article, and a component that returns null on a miss cannot do that.
	 */
	article: ArticleData;
}

/**
 * One body node. Images keep the photographer credit the exporter parsed out of the
 * markdown caption — dropping it here would put us in breach of the Unsplash and Pexels
 * terms on every article at once, which is exactly the failure the structured credit exists
 * to make impossible to introduce quietly.
 */
export function renderNode(node: ArticleNode, key: number, article?: ArticleData) {
	if (node.kind === "para") return <Para key={key}>{node.text}</Para>;
	if (node.kind === "image") {
		// The caption credits; the alt describes. Folding them into one string printed the
		// same sentence twice — in the figcaption, and again as alt text on a failed load.
		const creditTemplate = article
			? t(getBlogTranslations(article.locale), "blog.article.photoCredit", "Photo by {name} via {source}")
			: "Photo by {name} via {source}";
		const caption = node.credit ? fill(creditTemplate, { name: node.credit.name, source: node.credit.source }) : undefined;
		return <ArticlePhoto key={key} src={node.url} alt={node.alt} caption={caption} />;
	}
	if (node.kind === "table") return <ArticleTable key={key} headers={node.headers} rows={node.rows} />;
	if (node.kind === "list") return <ArticleList key={key} items={node.items} ordered={node.ordered} />;
	if (node.kind === "embed") return <ArticleEmbed key={key} src={node.src} height={node.height} title={node.title} />;
	if (node.kind === "heading") {
		// `DayHeading`'s green "Day N" label is route chrome — it belongs on an itinerary stop,
		// not on a listicle section. `article?.articleType` is the same field `BlogArticleRoute`
		// switches the whole page layout on; see its doc comment in `articleData.ts` for why this
		// can't be derived from `stops` or from the heading text instead.
		if (article?.articleType === "itinerary")
			return (
				<DayHeading key={key} id={node.id} label={node.label}>
					<RichText text={node.text} />
				</DayHeading>
			);
		// The exporter's day-detection regex (`DAY_LABEL_RE`) is a pattern match on the heading
		// text, not a check of which article it is in — a listicle section literally titled
		// "Day 1 in Rome: what to see" matches it and gets split into `label` ("Day 1") and
		// `text` ("in Rome: what to see") exactly like a real itinerary stop would.
		// `ArticleSubHeading` has no label slot (on purpose — see its doc comment), so rendering
		// `node.text` alone here would silently drop "Day 1" off the page. Recombine them instead
		// of choosing one. The join is on a space, which restores every word but not necessarily
		// the original punctuation: the regex eats one optional separator (`[.:–—-]`) between the
		// label and the rest, so "Day 2. Khlongs" comes back as "Day 2 Khlongs". Words are what
		// matter here — this branch exists so nothing falls off the page, and re-deriving the
		// separator would mean second-guessing a regex that lives in another repo.
		const headingText = node.label ? `${node.label} ${node.text}` : node.text;
		return (
			<ArticleSubHeading key={key} id={node.id}>
				<RichText text={headingText} />
			</ArticleSubHeading>
		);
	}
	// A node kind this build does not know. It happens whenever a content sync lands ahead of
	// the deploy that teaches the page the new kind — the ordering is not enforced anywhere and
	// the two ship from different repos, so it is a matter of minutes, not of never.
	//
	// Falling through to renderBlock would look up `BLOCK_COMPONENTS[undefined]` and render an
	// empty UnimplementedBlock: the content disappears rather than degrading. Absent content is
	// the failure nobody reports, so degrade to prose instead and keep the words on the page.
	if (!(node as { type?: string }).type) {
		const unknown = node as { text?: unknown; items?: unknown };
		const text =
			typeof unknown.text === "string"
				? unknown.text
				: Array.isArray(unknown.items)
					? unknown.items.filter((i): i is string => typeof i === "string").join(", ")
					: null;
		if (process.env.NODE_ENV !== "production") {
			console.warn("[Article] unknown node kind, rendered as prose:", (node as { kind?: string }).kind);
		}
		return text ? <Para key={key}>{text}</Para> : null;
	}

	return renderBlock(node, key, article ? { slug: article.slug, category: article.category, geo: article.geo, locale: article.locale } : undefined);
}

function renderSection(section: ArticleSection, index: number, article?: ArticleData) {
	return (
		<div key={section.id}>
			{/* `NumberedHeading` takes an optional `n` for listicles ("3. Daigo-ji"). Nothing
			    sets it yet: the exporter has no notion of a numbered section, and inferring it
			    from the category would number the wrong articles. Left plain until a listicle
			    goes through the pipeline and the shape is real rather than guessed. */}
			<NumberedHeading id={section.id}>{section.heading}</NumberedHeading>
			{section.content.map((node, i) => renderNode(node, index * 1000 + i, article))}
		</div>
	);
}

/** Which of the three related-article surfaces a reader actually uses is its own question. */
const trackRelated = (slug: string, placement: "sidebar" | "footer") => trackEvent("blog_related_clicked", { slug, placement }, "blog");

const DiscoverArticle = ({ article, localeSlugs }: DiscoverArticleProps) => {
	const history = useHistory();
	// The article's own locale drives the chrome strings — it is the language of the resolved
	// content, which is what a reader on `/nl/blog/<slug>` is looking at.
	const tr = getBlogTranslations(article.locale);
	// Same source for related-card hrefs: `article.locale` is correct during SSR, unlike
	// `window.location.pathname`. It's a plain `string` off the knowledge-base document, so
	// narrow it before handing it to `buildLocalizedPath` (typed against `MARKETING_LANG_CODES`).
	const articleLang = isMarketingLangCode(article.locale) ? article.locale : "en";

	// One view per article, not per render. `slug` is the identity; `geo`, `category` and
	// `locale` are the dimensions the question "which content earns its place" is sliced by.
	useEffect(() => {
		trackEvent("blog_article_viewed", { slug: article.slug, geo: article.geo ?? undefined, category: article.category, locale: article.locale }, "blog");
	}, [article.slug, article.geo, article.category, article.locale]);

	const heroPhoto = article.hero?.url;
	// `article.hero.alt` is the KB's reader-facing description of the same photo — `ArticlePhoto`
	// already renders it for every body image, but the hero (top banner + wave CTA below) dropped
	// it until 123yb6937nz. Falls back to the article title, the same fallback
	// `BlogArticle.tsx`'s legacy `headerAlt` uses for the Strapi corpus.
	const heroPhotoAlt = article.hero?.alt || article.title;
	const lead = article.intro.find((n): n is Extract<ArticleNode, { kind: "para" }> => n.kind === "para");
	const rest = article.intro.filter((n) => n !== lead);
	const countryLabel = article.breadcrumbs.find((c) => c.url?.startsWith("/discover/"))?.name;
	const meta = [
		`${article.readingMinutes} ${t(tr, "blog.minRead", "min read")}`,
		article.lastUpdated ? `${t(tr, "blog.article.updated", "Updated")} ${article.lastUpdated}` : null,
	]
		.filter(Boolean)
		.join(" · ");

	return (
		<GuideUiContext.Provider value={tr}>
			{/* `articleLang`, the same SSR-correct locale the breadcrumb/related-article hrefs above
			    already use — not `useMarketingLang()`, which falls back to "en" during SSR (see the
			    `GuideLangContext` doc comment in discoverSections.tsx). Gives `RichText`'s in-body
			    prose links the same locale prefix the read-more/CTA blocks already carry
			    (123yb692y18). */}
			<GuideLangContext.Provider value={articleLang}>
				<div id="stippl-discover-article" className={styles.page}>
					<ReadingProgressBar />
					{/* The switcher lived only on `BlogArticle`, the Strapi renderer. Every article is a
				    knowledge-base one now, so the blog had no way to change language at all
				    (123yb691znj). `localeSlugs` comes from the article's own translations map. */}
					<Navigation impact={true} langMenu={<LanguageSwitcher type="article" localeSlugs={localeSlugs} />} />

					<ArticleHero
						countryCode={article.countrySlug ? alpha2For(countryLabel || article.countrySlug) : undefined}
						countryLabel={countryLabel}
						crumbs={[
							// Both of these dropped a German reader into English. The first was a hardcoded
							// "/blog"; the rest carry `/discover/...` URLs the knowledge base writes without a
							// locale, by design — see `localizeHref`.
							{ label: t(tr, "blog.article.crumbBlog", "Blog"), onClick: () => history.push(buildLocalizedPath("/blog", articleLang)) },
							...article.breadcrumbs
								.slice(1, -1)
								.map((c) => ({ label: c.name, onClick: c.url ? () => history.push(localizeHref(c.url as string, articleLang)) : undefined })),
							{ label: article.title },
						]}
						title={article.title}
						description={lead?.text}
						author={bylineOf(article.author)}
						meta={meta}
						heroPhoto={heroPhoto}
						heroPhotoAlt={heroPhotoAlt}
						shareTitle={article.title}
						shareLabels={{
							share: t(tr, "blog.article.share", "Share"),
							copyLink: t(tr, "blog.article.copyLink", "Copy link"),
							shareOn: t(tr, "blog.article.shareOn", "Share on"),
						}}
					/>

					<div className={styles.contentWrap}>
						<div className={styles.contentGrid}>
							<article className={styles.article}>
								{rest.map((node, i) => renderNode(node, i, article))}
								{/* work/install-card-mobile.md's A3: after the intro, before the reader is deep
								    into the sections. Mobile/tablet only (CSS-hidden on desktop, see
								    InlineInstallCard.scss). `countryLabel` is undefined on articles with no
								    resolved country (e.g. /blog/best-ai-travel-planner-2026) — the card falls
								    back to a country-agnostic title rather than rendering nothing. */}
								<InlineInstallCard country={countryLabel} locale={article.locale} />
								{article.sections.map((s, i) => renderSection(s, i, article))}
								{article.faq.length > 0 && (
									<Faq
										guide={{
											name: countryLabel || article.title,
											faqs: article.faq.map((f) => ({ q: f.question, a: f.answer })),
										}}
										kicker={t(tr, "blog.article.faqKicker", "Everything else")}
										title={fill(t(tr, "blog.article.faqTitle", "Frequently asked questions about {name}"), { name: countryLabel || article.title })}
									/>
								)}
								{(article.outro || []).length > 0 && (
									<div className={styles.outro}>{article.outro!.map((node, i) => renderNode(node, 900000 + i, article))}</div>
								)}
							</article>

							<ArticleSidebar
								toc={article.toc}
								author={bylineOf(article.author)}
								tocLabel={t(tr, "blog.article.tocLabel", "In this guide")}
								authorLabel={t(tr, "blog.article.aboutAuthor", "About the author")}
								authorCtaLabel={t(tr, "blog.article.openInStippl", "Open in Stippl →")}
								authorCtaModalTitle={t(tr, "blog.article.openInStipplModalTitle", "Create a free account to open this in Stippl")}
								relatedLabel={t(tr, "blog.article.keepReading", "Keep reading")}
								relatedReads={article.related.slice(0, 3).map((r) => ({
									title: r.title,
									photo: r.image,
									href: buildLocalizedPath(`/blog/${r.slug}`, articleLang),
									onClick: () => trackRelated(r.slug, "sidebar"),
								}))}
							/>
						</div>
					</div>

					{/* The download CTA leans on the hero photo and the country. An article with
				    neither — most of the legacy corpus — gets no CTA rather than an empty one. */}
					{heroPhoto && countryLabel && (
						<WaveDownloadCTA
							photo={heroPhoto}
							alt={heroPhotoAlt}
							country={countryLabel}
							title={fill(t(tr, "blog.article.ctaTitle", "Plan {country} in Stippl"), { country: countryLabel })}
							body={fill(
								t(tr, "blog.article.ctaBodyGuide", "Everything in this guide, ready to drop into your {country} route, with planning and bookings in one app."),
								{ country: countryLabel }
							)}
							kicker={t(tr, "blog.article.ctaKicker", "Ready when you are")}
							scanLabel={t(tr, "blog.article.scanMe", "Scan me")}
							scanAlt={t(tr, "blog.article.scanAlt", "Scan to download the Stippl app")}
						/>
					)}

					{article.related.length > 0 && (
						<RelatedArticles
							heading={t(tr, "blog.article.relatedHeading", "Discover more adventures")}
							kicker={t(tr, "blog.article.relatedKicker", "Read on")}
							readLabel={t(tr, "blog.article.readSuffix", "read")}
							articles={article.related.map((r) => ({
								onClick: () => trackRelated(r.slug, "footer"),
								title: r.title,
								tag: r.category,
								photo: r.image,
								read: r.readingMinutes ? `${r.readingMinutes} ${t(tr, "blog.article.minAbbr", "min")}` : undefined,
								href: buildLocalizedPath(`/blog/${r.slug}`, articleLang),
							}))}
						/>
					)}

					<Footer />
					{/* The KB layout branch of /blog/:article (bugs/blog-install-card-never-renders.md):
					    BlogArticleRoute picks this component, ItineraryArticle, or the legacy
					    BlogArticle — only BlogArticle rendered this card, so every knowledge-base
					    article never showed it. Renders unconditionally, including on articles that
					    also show WaveDownloadCTA above: the card reveals at ~500px (~5% into a long
					    article) while the wave CTA sits at ~82% depth, so gating it off handed the
					    install affordance to only the readers who finish. Reach beats avoiding the
					    stacked QR — do not re-add a gate. */}
					<DownloadAppQrCard />
				</div>
			</GuideLangContext.Provider>
		</GuideUiContext.Provider>
	);
};

export default DiscoverArticle;
